import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { TableLink } from '@/components/global';
import { apiFetch } from '@/lib/api';
import { BatchSelect, type BatchSelectOption } from '@/components/batches';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { SummaryMetricCard, MetricGrid } from '@/components/global/SummaryMetricCard';
import { WarehouseTypeBadge, LocationLink } from '@/components/locations';
import { MachineLink } from '@/components/machines';
import { ProductIdentity } from '@/components/products';
import { ArrowLeft, Cpu, Package, Warehouse } from 'lucide-react';

interface BatchGroupRow {
  groupKey: string;
  productId: string;
  batchNumber: string;
  totalQuantityRemaining: number;
  expiryDate: string | null;
  product?: {
    id: string;
    sku: string;
    name: string;
  };
}

interface BatchTraceResult {
  batchNumber: string;
  product: { id: string; sku: string; name: string; imageUrl: string | null } | null;
  warehousePositions: Array<{
    warehouseId: string;
    warehouseName: string;
    warehouseType: string;
    quantityRemaining: number;
    expiryDate: string | null;
  }>;
  machinePositions: Array<{
    machineId: string;
    machineName: string;
    venueName: string | null;
    quantityRemaining: number;
    expiryDate: string | null;
  }>;
  recentMovements: Array<{
    id: string;
    createdAt: string;
    movementType: string;
    quantity: number;
    warehouseName: string | null;
    referenceType: string | null;
    referenceId: string | null;
  }>;
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('en-GB');
}

function formatDateTime(value: string): string {
  return new Date(value).toLocaleString('en-GB');
}

export default function BatchRecallPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlBatchNumber = searchParams.get('batchNumber') ?? '';
  const urlProductId = searchParams.get('productId') ?? '';
  const [batchOptions, setBatchOptions] = useState<BatchSelectOption[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [selectedKey, setSelectedKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<BatchTraceResult | null>(null);

  useEffect(() => {
    let cancelled = false;
    setOptionsLoading(true);
    apiFetch('/api/v1/batches?grouped=true&pageSize=500')
      .then(async (res) => {
        if (!res.ok || cancelled) return;
        const data = await res.json();
        const groups: BatchGroupRow[] = data.items ?? [];
        if (cancelled) return;
        setBatchOptions(
          groups.map((g) => ({
            id: g.groupKey,
            batchNumber: g.batchNumber,
            quantityRemaining: g.totalQuantityRemaining,
            expiryDate: g.expiryDate,
            productId: g.productId,
            productSku: g.product?.sku ?? null,
            productName: g.product?.name ?? null,
          }))
        );
      })
      .catch(() => {
        if (!cancelled) setBatchOptions([]);
      })
      .finally(() => {
        if (!cancelled) setOptionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const runTrace = useCallback(async (batchNumber: string, productId?: string) => {
    const normalized = batchNumber.trim();
    if (!normalized) return;

    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ batchNumber: normalized });
      if (productId) params.set('productId', productId);
      const res = await apiFetch(`/api/v1/batches/trace?${params}`);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setResult(null);
        setError(typeof data.error === 'string' ? data.error : 'No stock found for this batch');
        return;
      }
      setResult(await res.json());
    } catch {
      setResult(null);
      setError('Failed to trace batch');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const trimmed = urlBatchNumber.trim();
    if (!trimmed) {
      setSelectedKey('');
      setResult(null);
      setError(null);
      return;
    }

    const matched = batchOptions.find(
      (o) =>
        o.batchNumber.toLowerCase() === trimmed.toLowerCase()
        && (!urlProductId || o.productId === urlProductId)
    );
    setSelectedKey(matched?.id ?? trimmed);

    void runTrace(trimmed, urlProductId || matched?.productId);
  }, [urlBatchNumber, urlProductId, batchOptions, runTrace]);

  const handleBatchSelect = (value: string, option?: BatchSelectOption) => {
    if (option) {
      setSelectedKey(option.id);
      const params: Record<string, string> = { batchNumber: option.batchNumber };
      if (option.productId) params.productId = option.productId;
      setSearchParams(params);
      return;
    }

    const normalized = value.trim();
    if (!normalized) {
      setSelectedKey('');
      setSearchParams({});
      return;
    }

    setSelectedKey(normalized);
    setSearchParams({ batchNumber: normalized });
  };

  const warehouseTotal = useMemo(
    () => result?.warehousePositions.reduce((sum, p) => sum + p.quantityRemaining, 0) ?? 0,
    [result]
  );
  const machineTotal = useMemo(
    () => result?.machinePositions.reduce((sum, p) => sum + p.quantityRemaining, 0) ?? 0,
    [result]
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <Button variant="ghost" size="sm" className="-ml-2 mb-2" asChild>
            <Link to="/batches">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Batch tracking
            </Link>
          </Button>
          <h1 className="text-2xl font-semibold">Batch recall trace</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Locate a supplier batch across hubs, spokes, and machines. Use this during product
            recalls or expiry investigations.
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Search by batch number</CardTitle>
          <CardDescription>
            Enter the supplier batch code from the carton label or goods receipt.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="max-w-lg space-y-2">
            <p className="text-sm font-medium">Supplier batch</p>
            <BatchSelect
              value={selectedKey}
              matchValue="id"
              batches={batchOptions}
              onValueChange={handleBatchSelect}
              disabled={optionsLoading}
              placeholder={optionsLoading ? 'Loading batches…' : 'Search or scan batch…'}
              scanDescription="Scan the supplier batch label to trace stock across the estate."
              contentClassName="min-w-[min(calc(100vw-2rem),24rem)]"
            />
          </div>
          {error && (
            <p className="mt-4 text-sm text-destructive">{error}</p>
          )}
        </CardContent>
      </Card>

      {result && (
        <>
          <div className="rounded-lg border bg-card p-4">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
              <span className="font-mono text-lg font-semibold">{result.batchNumber}</span>
              {result.product && (
                <ProductIdentity
                  productId={result.product.id}
                  name={result.product.name}
                  sku={result.product.sku}
                  imageUrl={result.product.imageUrl}
                  size="sm"
                />
              )}
            </div>
          </div>

          <MetricGrid>
            <SummaryMetricCard
              label="In warehouses"
              value={warehouseTotal.toLocaleString()}
              icon={Warehouse}
              isLoading={loading}
            />
            <SummaryMetricCard
              label="In machines"
              value={machineTotal.toLocaleString()}
              icon={Cpu}
              isLoading={loading}
            />
            <SummaryMetricCard
              label="Total on hand"
              value={(warehouseTotal + machineTotal).toLocaleString()}
              icon={Package}
              isLoading={loading}
            />
          </MetricGrid>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Warehouse stock</CardTitle>
                <CardDescription>Hub and spoke locations with remaining batch quantity.</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Location</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Expiry</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {result.warehousePositions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                          No warehouse stock for this batch.
                        </TableCell>
                      </TableRow>
                    ) : (
                      result.warehousePositions.map((pos) => (
                        <TableRow key={pos.warehouseId}>
                          <TableCell>
                            <LocationLink
                              locationType={pos.warehouseType}
                              locationId={pos.warehouseId}
                              name={pos.warehouseName}
                            />
                          </TableCell>
                          <TableCell>
                            <WarehouseTypeBadge type={pos.warehouseType as 'HUB' | 'SPOKE'} />
                          </TableCell>
                          <TableCell>{formatDate(pos.expiryDate)}</TableCell>
                          <TableCell className="text-right font-mono">
                            {pos.quantityRemaining.toLocaleString()}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Machine stock</CardTitle>
                <CardDescription>Units loaded into vending machines from this batch.</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Machine</TableHead>
                      <TableHead>Venue</TableHead>
                      <TableHead>Expiry</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {result.machinePositions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                          No machine stock for this batch.
                        </TableCell>
                      </TableRow>
                    ) : (
                      result.machinePositions.map((pos) => (
                        <TableRow key={pos.machineId}>
                          <TableCell>
                            <MachineLink machineId={pos.machineId} name={pos.machineName} />
                          </TableCell>
                          <TableCell>{pos.venueName ?? '—'}</TableCell>
                          <TableCell>{formatDate(pos.expiryDate)}</TableCell>
                          <TableCell className="text-right font-mono">
                            {pos.quantityRemaining.toLocaleString()}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Recent movements</CardTitle>
              <CardDescription>
                Latest ledger entries for this batch.{' '}
                <TableLink to="/inventory/movements">View full movements ledger</TableLink>
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Warehouse</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    <TableHead>Reference</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {result.recentMovements.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                        No movements recorded for this batch.
                      </TableCell>
                    </TableRow>
                  ) : (
                    result.recentMovements.map((m) => (
                      <TableRow key={m.id}>
                        <TableCell>{formatDateTime(m.createdAt)}</TableCell>
                        <TableCell>{m.warehouseName ?? '—'}</TableCell>
                        <TableCell>{m.movementType}</TableCell>
                        <TableCell className="text-right font-mono">{m.quantity}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {m.referenceType ?? '—'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
